import appInsights from 'app/components/AppInsights.tsx';

type LogValue = unknown;

type SanitizeOptions = {
  preserveErrors?: boolean;
};

type AxiosLikeError = Error & {
  isAxiosError?: boolean;
  status?: number;
  config?: {
    url?: unknown;
    method?: unknown;
  };
  response?: {
    status?: number;
    data?: unknown;
  };
};

interface Logger {
  debug: (message?: LogValue, ...optionalParams: LogValue[]) => void;
  info: (message?: LogValue, ...optionalParams: LogValue[]) => void;
  warn: (message?: LogValue, ...optionalParams: LogValue[]) => void;
  error: (message?: LogValue, ...optionalParams: LogValue[]) => void;
  log: (message?: LogValue, ...optionalParams: LogValue[]) => void;
}

const MAX_LOG_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 20;
const MAX_STRING_LENGTH = 2000;

const truncateString = (value: string, maxLength = MAX_STRING_LENGTH): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}…[truncated ${value.length - maxLength} chars]`;
};

const getObjectTag = (value: unknown): string => Object.prototype.toString.call(value);

const isAxiosError = (error: Error): error is AxiosLikeError => {
  return 'isAxiosError' in error && (error as AxiosLikeError).isAxiosError === true;
};

const sanitizeLogValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
  options: SanitizeOptions = {}
): unknown => {
  if (value == null) {
    return value;
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }

  if (typeof Event !== 'undefined' && value instanceof Event) {
    const baseEvent = {
      type: value.type,
      isTrusted: value.isTrusted,
      target: value.target != null ? String(value.target) : undefined,
      currentTarget: value.currentTarget != null ? String(value.currentTarget) : undefined
    };

    if (typeof CloseEvent !== 'undefined' && value instanceof CloseEvent) {
      return {
        ...baseEvent,
        code: value.code,
        reason: value.reason,
        wasClean: value.wasClean
      };
    }

    if (typeof MessageEvent !== 'undefined' && value instanceof MessageEvent) {
      return {
        ...baseEvent,
        data: sanitizeLogValue(value.data, depth + 1, seen, options)
      };
    }

    return baseEvent;
  }

  if (typeof WebSocket !== 'undefined' && value instanceof WebSocket) {
    return {
      type: 'WebSocket',
      url: value.url,
      readyState: value.readyState,
      protocol: value.protocol,
      extensions: value.extensions,
      bufferedAmount: value.bufferedAmount
    };
  }

  if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) {
    return `<${value.tagName.toLowerCase()} id="${value.id}" class="${value.className}">`;
  }

  if (depth >= MAX_LOG_DEPTH) {
    return `[MaxDepth:${getObjectTag(value)}]`;
  }

  if (seen.has(value as object)) {
    return '[Circular]';
  }

  seen.add(value as object);

  if (value instanceof Error) {
    return options.preserveErrors ? value : serializeErrorForLog(value, depth, seen);
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeLogValue(item, depth + 1, seen, options));

    if (value.length > MAX_ARRAY_ITEMS) {
      sanitizedItems.push(`…[${value.length - MAX_ARRAY_ITEMS} more items]`);
    }

    return sanitizedItems;
  }

  const sanitizedObject: Record<string, unknown> = {};
  const keys = Object.keys(value as Record<string, unknown>);

  for (const key of keys.slice(0, MAX_OBJECT_KEYS)) {
    try {
      sanitizedObject[key] = sanitizeLogValue((value as Record<string, unknown>)[key], depth + 1, seen, options);
    } catch (error) {
      sanitizedObject[key] = `[Unreadable: ${String(error)}]`;
    }
  }

  if (keys.length > MAX_OBJECT_KEYS) {
    sanitizedObject.__truncatedKeys = `${keys.length - MAX_OBJECT_KEYS} more keys`;
  }

  return sanitizedObject;
};

const serializeErrorForLog = (error: Error, depth = 0, seen = new WeakSet<object>()): Record<string, unknown> => {
  const serializedError: Record<string, unknown> = {
    name: error.name,
    message: truncateString(error.message),
    stack: truncateString(error.stack ?? '')
  };

  const errorRecord = error as unknown as Record<string, unknown>;
  const keys = Object.keys(errorRecord);

  for (const key of keys.slice(0, MAX_OBJECT_KEYS)) {
    try {
      serializedError[key] = sanitizeLogValue(errorRecord[key], depth + 1, seen);
    } catch (serializationError) {
      serializedError[key] = `[Unreadable: ${String(serializationError)}]`;
    }
  }

  if (keys.length > MAX_OBJECT_KEYS) {
    serializedError.__truncatedKeys = `${keys.length - MAX_OBJECT_KEYS} more keys`;
  }

  if (isAxiosError(error)) {
    const status = error.response?.status ?? error.status;

    if (status !== undefined) {
      serializedError.status = status;
    }

    serializedError.config = {
      url: error.config?.url != null ? String(error.config.url) : undefined,
      method: error.config?.method != null ? String(error.config.method) : undefined
    };

    if (error.response != null) {
      serializedError.response = {
        status: error.response.status,
        data: sanitizeLogValue(error.response.data, depth + 1, seen)
      };
    }
  }

  return serializedError;
};

export const safeSerializeForLog = (value: unknown): string => {
  const sanitizedValue = sanitizeLogValue(value);

  if (typeof sanitizedValue === 'string') {
    return sanitizedValue;
  }

  try {
    return JSON.stringify(sanitizedValue);
  } catch {
    return String(sanitizedValue);
  }
};

export const getSafeErrorLog = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return serializeErrorForLog(error);
  }

  return {
    message: safeSerializeForLog(error)
  };
};

export const getErrorForLog = (error: unknown, fallbackMessage = 'Unknown error'): Error => {
  if (error instanceof Error) {
    return error;
  }

  const serializedError = safeSerializeForLog(error);
  const normalizedError = new Error(serializedError || fallbackMessage);
  normalizedError.name = 'NonErrorThrown';

  return normalizedError;
};

const convertTelemetryProperties = (optionalParams: LogValue[]): Record<string, unknown> | undefined => {
  if (optionalParams.length === 0) {
    return undefined;
  }

  if (optionalParams.length === 1) {
    const sanitizedValue = sanitizeLogValue(optionalParams[0]);

    if (
      sanitizedValue != null &&
      typeof sanitizedValue === 'object' &&
      !Array.isArray(sanitizedValue)
    ) {
      return sanitizedValue as Record<string, unknown>;
    }

    return { value: safeSerializeForLog(sanitizedValue) };
  }

  return { details: safeSerializeForLog(optionalParams) };
};

const toSafeException = (exception: Error): Error => {
  const safeException = new Error(exception.message);
  safeException.name = exception.name;
  safeException.stack = exception.stack;

  return safeException;
};

const trackException = (exception: Error, optionalParams: LogValue[], severityLevel: number) => {
  appInsights.trackException({ exception: toSafeException(exception), severityLevel }, convertTelemetryProperties(optionalParams));
};

const trackTrace = (message: LogValue, optionalParams: LogValue[], severityLevel: number) => {
  const msg = typeof message === 'string' ? truncateString(message) : safeSerializeForLog(message);
  appInsights.trackTrace({ message: msg, severityLevel }, convertTelemetryProperties(optionalParams));
};

const appInsightsLog = (message: LogValue, optionalParams: LogValue[], severityLevel: number) => {
  if (message instanceof Error) {
    trackException(message, optionalParams, severityLevel);
  } else {
    trackTrace(message, optionalParams, severityLevel);
  }
};

const consoleLog = (method: keyof Console, message: LogValue, optionalParams: LogValue[]) => {
  const logFn = console[method] as (...args: unknown[]) => void;

  try {
    logFn(
      message instanceof Error ? message : sanitizeLogValue(message),
      ...optionalParams.map((param) => (param instanceof Error ? param : sanitizeLogValue(param)))
    );
  } catch {
    logFn(String(message), ...optionalParams.map((param) => String(param)));
  }
};

/**
 * Custom logger to log to both console and Application Insights
 */
const logger: Logger = {
  debug: (message, ...optionalParams) => {
    consoleLog('debug', message, optionalParams);
    appInsightsLog(message, optionalParams, 0);
  },
  info: (message, ...optionalParams) => {
    consoleLog('info', message, optionalParams);
    appInsightsLog(message, optionalParams, 1);
  },
  warn: (message, ...optionalParams) => {
    consoleLog('warn', message, optionalParams);
    appInsightsLog(message, optionalParams, 2);
  },
  error: (message, ...optionalParams) => {
    consoleLog('error', message, optionalParams);
    appInsightsLog(message, optionalParams, 3);
  },
  log: (message, ...optionalParams) => {
    consoleLog('log', message, optionalParams);
    appInsightsLog(message, optionalParams, 1);
  }
};

export default logger;
